import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Recommendations from '../components/Recommendations';
import FailuresPreventedGraph from '../components/FailuresPreventedGraph';
import MaintainenceCostSavedGraph from '../components/MaintainenceCostSavedGraph';
import UnplannedDownTimeAvoidedGraph from '../components/UnplannedDownTimeAvoidedGraph';
import WeeklyDowntimeGraph from '../components/WeeklyDowntimeGraph';
import AverageMaintenanceCostGraph from '../components/AverageMaintenanceCostGraph';
import StatusStackedBarGraph from '../components/StatusStackedBarGraph';
import VehicleAgeGraph from '../components/VehicleAgeGraph';
import CompliancesGraph from '../components/CompliancesGraph';
import TopFailuresGraph from '../components/TopFailuresGraph';
import TruckHealthOverview from '../components/TruckHealthOverview';
import DatePickerInput from '../components/DatePickerInput';
import '../styles/styles.css';
import posthog from 'posthog-js';

const Dashboard = () => {
  const formatDateToMDY = (date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const defaultRestrictedDate = formatDateToMDY(yesterday);
  const defaultUnrestrictedDate = '04/30/2025';

  const restrictedDevice = process.env.REACT_APP_RESTRICTED_DEVICE || '';
  const deviceProvider = localStorage.getItem("tensor-user-device-provider");
  const isRestricted = deviceProvider === restrictedDevice;

  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = localStorage.getItem('fleet-selected-date');
    const isValidSavedDate = savedDate &&
      !isNaN(new Date(savedDate).getTime()) &&
      new Date(savedDate) >= new Date('2025-05-15') &&
      new Date(savedDate) <= yesterday;

    return isValidSavedDate
      ? savedDate
      : isRestricted
        ? defaultRestrictedDate
        : defaultUnrestrictedDate;
  });

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedAgeRange, setSelectedAgeRange] = useState(null);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const [selectedFailedComponent, setSelectedFailedComponent] = useState(null);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    localStorage.setItem('fleet-selected-date', date);
  };

  useEffect(() => {
    const start = Date.now();
    return () => {
      const end = Date.now();
      posthog.capture('time_on_dashboard', {
        duration: Math.round((end - start) / 1000),
        user_id: localStorage.getItem("tensor-user-id") || "unknown",
        page: '/dashboard',
      });
    };
  }, []);

  const graphProps = () => ({
    selectedDate,
    selectedStatus,
    selectedAgeRange,
  });

  const FilterPill = ({ label, onClear }) => (
    <div className="inline-flex items-center px-2.5 py-0.5 bg-[#032FCE] text-[#FCFDFE] text-[12px] font-medium rounded-[6px] mr-2 mb-2">
      <span className="mr-2 capitalize">{label}</span>
      <button
        onClick={() => {
          posthog.capture('filter_removed', { filter: label });
          onClear();
        }}
        className="hover:opacity-80 focus:outline-none text-[12px] text-[#FCFDFE]"
      >
        ×
      </button>
    </div>
  );

  const GraphWrapper = ({ children }) => (
    <div className="flex-1 min-w-[0]">{children}</div>
  );

  const formatFailedComponent = (value) => {
    const [component, time] = value.split(':');
    const formattedComponent = component.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
    const weeks = time?.replace(/\D/g, '');
    if (time?.toLowerCase().startsWith('last')) return `${formattedComponent} (Last ${weeks} weeks)`;
    if (time?.toLowerCase().startsWith('next')) return `${formattedComponent} (Next ${weeks} weeks)`;
    return formattedComponent;
  };

  return (
    <div className="min-h-screen body_bg_color">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <DatePickerInput
          headerText="Fleet Statistics"
          initialDate={selectedDate}
          onDateChange={handleDateChange}
          isRestricted={isRestricted}
        />

        <div className="flex flex-wrap gap-2 my-2">
          {selectedStatus && (
            <FilterPill label={`status: ${selectedStatus.replace("_", " ")}`} onClear={() => setSelectedStatus(null)} />
          )}
          {selectedAgeRange && (
            <FilterPill label={`age: ${selectedAgeRange}`} onClear={() => setSelectedAgeRange(null)} />
          )}
          {selectedCompliance && (
            <FilterPill label={`compliance: ${selectedCompliance.replace("_", " ")}`} onClear={() => setSelectedCompliance(null)} />
          )}
          {selectedFailedComponent && (
            <FilterPill label={`Failed Component: ${formatFailedComponent(selectedFailedComponent)}`} onClear={() => setSelectedFailedComponent(null)} />
          )}
        </div>

        {/* Top Summary Section */}
        <section className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:max-w-[492px] min-w-[300px]">
            <div className="bg-white p-4 rounded shadow">
              <Recommendations {...graphProps()} />
            </div>
          </div>
          <div className="flex-1 min-w-[300px]">
            <div className="flex flex-col lg:flex-row gap-2">
              <GraphWrapper><FailuresPreventedGraph {...graphProps()} /></GraphWrapper>
              <GraphWrapper><MaintainenceCostSavedGraph {...graphProps()} /></GraphWrapper>
              <GraphWrapper><UnplannedDownTimeAvoidedGraph {...graphProps()} /></GraphWrapper>
            </div>
          </div>
        </section>

        {/* Main Dashboard Graphs */}
        <section className="flex flex-col md:flex-row gap-6 mt-6 items-stretch">
          {/* Left Column */}
          <div className="w-full md:max-w-[492px] min-w-[300px] flex flex-col gap-6">
            <div className="bg-white p-4 rounded shadow overflow-auto">
              <h3 className="text-lg font-semibold mb-1 block_head">Vehicles In The Fleet</h3>
              <StatusStackedBarGraph {...graphProps()} onStatusChange={setSelectedStatus} />
            </div>

            <div className="bg-white p-4 rounded shadow overflow-auto">
              <h3 className="text-lg font-semibold mb-2 block_head">Average Vehicle Age</h3>
              <VehicleAgeGraph {...graphProps()} onAgeRangeChange={setSelectedAgeRange} />
            </div>

            <CompliancesGraph
              {...graphProps()}
              selectedCompliance={selectedCompliance}
              onSelectedComplianceChange={setSelectedCompliance}
            />
          </div>

          {/* Right Column */}
          <div className="flex flex-col flex-1 gap-6">
            <GraphWrapper>
              <WeeklyDowntimeGraph {...graphProps()} />
            </GraphWrapper>
            <GraphWrapper>
              <AverageMaintenanceCostGraph {...graphProps()} />
            </GraphWrapper>
            <TopFailuresGraph
              {...graphProps()}
              selectedFailedComponent={selectedFailedComponent}
              onSelectedFailedComponentChange={setSelectedFailedComponent}
            />
          </div>
        </section>

        {/* Truck Health */}
        <section>
          <TruckHealthOverview
            {...graphProps()}
            selectedCompliance={selectedCompliance}
            selectedFailedComponent={selectedFailedComponent}
          />
        </section>
      </main>

      <footer className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-2 py-4 text-sm text-center text-gray-600">
          © 2025 Tensor Planet. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
